/**
 * A conclusão da CCB no alto da mesa (E162.F2.T02, QA-18, ADR-0111 §2.1 e §2.6).
 *
 * A mesa pedia grau e redação por documento — até da selfie — e escondia a saída da consulta
 * travada em outra tela. Agora o alto da mesa é a decisão que o caso espera: só o que pede
 * conclusão (a CCB, a peça sem tipo), com o contador dela e, quando a consulta do sistema trava,
 * "Registrar limitação" logo abaixo do concluir. Os apoios não aparecem aqui: são conferidos com a
 * verificação e ficam no detalhe por documento.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { ColunaDeDecisao } from '@/components/ExameEletronico/ColunaDeDecisao';
import { contadorDasCcbs } from '@/components/ExameEletronico/contadorDasCcbs';
import type { DecisaoAcao } from '@/components/ExameEletronico/DecisaoDoPerito';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ConclusaoEletronicaInput, ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { exigeConclusao } from '@/lib/exame_eletronico_ccb';
import { tituloDaPeca } from '@/lib/exame_eletronico_peca';

import { ConsultasQueTravam } from './ConsultasQueTravam';

const t = ptBR.mesaEletronica;

type PorCcb<T> = Readonly<Record<string, T>>;

export interface ConclusaoDaCcbProps {
  client: AxiosInstance;
  caseId: string;
  pecas: readonly ExameEletronicoCcbData[];
  todasConcluidas: boolean;
  acaoPorCcb: PorCcb<DecisaoAcao>;
  erroPorCcb: PorCcb<string>;
  sucessoPorCcb: PorCcb<string>;
  onConcluir: (docId: string, conclusao: ConclusaoEletronicaInput) => void;
  onReabrir: (docId: string, motivo: string) => void;
  /** A limitação registrada ou a nova tentativa mudou o caso: a mesa relê. */
  onMudou: () => void;
}

function EscolherCcb({
  pecas,
  aConcluir,
  aberta,
  onEscolher,
}: {
  pecas: readonly ExameEletronicoCcbData[];
  aConcluir: readonly ExameEletronicoCcbData[];
  aberta: string;
  onEscolher: (docId: string) => void;
}): JSX.Element {
  return (
    <div role="group" aria-label={t.escolherCcb} className="flex flex-wrap gap-sc-2">
      {aConcluir.map((peca) => (
        <Button
          key={peca.source_document_id}
          type="button"
          size="sm"
          variant={peca.source_document_id === aberta ? 'primary' : 'secondary'}
          aria-pressed={peca.source_document_id === aberta}
          onClick={() => onEscolher(peca.source_document_id)}
          data-testid={`mesa-escolher-ccb-${peca.source_document_id}`}
        >
          {tituloDaPeca(peca, pecas.indexOf(peca))}
        </Button>
      ))}
    </div>
  );
}

export function ConclusaoDaCcb(props: ConclusaoDaCcbProps): JSX.Element {
  const { pecas, acaoPorCcb, erroPorCcb, sucessoPorCcb } = props;
  const aConcluir = pecas.filter(exigeConclusao);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const peca = aConcluir.find((p) => p.source_document_id === escolhida) ?? aConcluir[0];
  return (
    <section
      className="mt-sc-6 space-y-sc-4"
      data-testid="mesa-conclusao-da-ccb"
      aria-labelledby="mesa-conclusao-da-ccb-titulo"
    >
      <header>
        <h2 id="mesa-conclusao-da-ccb-titulo" className="text-display-sm text-fg">
          {t.conclusaoTitulo}
        </h2>
        <p className="mt-sc-2 text-body-sm text-fg" data-testid="mesa-contador-ccb">
          {contadorDasCcbs(pecas)}
        </p>
      </header>
      {peca === undefined ? (
        <p className="text-body-sm text-fg-muted" data-testid="mesa-sem-ccb">
          {t.semCcbAConcluir}
        </p>
      ) : (
        <>
          {aConcluir.length > 1 ? (
            <EscolherCcb
              pecas={pecas}
              aConcluir={aConcluir}
              aberta={peca.source_document_id}
              onEscolher={setEscolhida}
            />
          ) : null}
          <ColunaDeDecisao
            pecas={aConcluir}
            peca={peca}
            indice={pecas.indexOf(peca)}
            acaoEmCurso={acaoPorCcb[peca.source_document_id] ?? null}
            erro={erroPorCcb[peca.source_document_id]}
            sucesso={sucessoPorCcb[peca.source_document_id]}
            onConcluir={(conclusao) => props.onConcluir(peca.source_document_id, conclusao)}
            onReabrir={(motivo) => props.onReabrir(peca.source_document_id, motivo)}
            onSelecionar={setEscolhida}
          />
          <ConsultasQueTravam
            client={props.client}
            caseId={props.caseId}
            releitura={pecas}
            onMudou={props.onMudou}
          />
        </>
      )}
      {props.todasConcluidas ? (
        <p className="text-body-md text-fg" data-testid="mesa-eletronica-concluida">
          {t.todasConcluidas}
        </p>
      ) : null}
    </section>
  );
}
